import { useEffect, useState } from 'react'
import { BadgeCheck, ShieldX } from 'lucide-react'
import { queryKeys } from '@/lib/query-client'
import type { AdminPaymentSummary } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { PaymentProofViewer } from './PaymentProofViewer'
import { rejectPayment, verifyPayment } from './api'
import { describePaymentMethod, formatMoney } from './describe'
import styles from './VerifyPaymentDialog.module.css'

export type VerificationDecision = 'verify' | 'reject'

interface VerifyPaymentDialogProps {
  tripRequestId: number | null
  bookingReference: string | null
  payment: AdminPaymentSummary | null
  decision: VerificationDecision
  onClose: () => void
}

/**
 * An operator's decision on a payment a human has to check.
 *
 * The proof is shown inside the dialog rather than only in the drawer behind
 * it: the whole point is that the decision is made against the evidence, and an
 * operator should not have to remember what they saw a moment ago.
 *
 * Approving is deliberately not the default action of a single click somewhere
 * — it moves the booking to PAID and makes the amount refundable, so it gets a
 * dialog with the amount spelled out.
 */
export function VerifyPaymentDialog({
  tripRequestId,
  bookingReference,
  payment,
  decision,
  onClose,
}: VerifyPaymentDialogProps) {
  const open = tripRequestId !== null && payment !== null
  const [comment, setComment] = useState('')

  useEffect(() => {
    if (open) setComment('')
  }, [open, tripRequestId, decision])

  const rejecting = decision === 'reject'

  const mutation = useAdminMutation({
    mutationFn: () =>
      rejecting
        ? rejectPayment(tripRequestId!, comment.trim())
        : verifyPayment(tripRequestId!, comment.trim() || undefined),
    successTitle: rejecting ? 'Payment rejected' : 'Payment verified',
    successDescription: () =>
      rejecting
        ? `Booking ${bookingReference ?? ''} is marked unpaid, and any points it used have been returned.`.trim()
        : `Booking ${bookingReference ?? ''} is now marked as paid.`.trim(),
    errorTitle: rejecting ? 'The payment was not rejected' : 'The payment was not verified',
    // The trips list renders the same payment summary, so it is invalidated too
    // — otherwise a trip row keeps showing an unverified badge on a payment the
    // operator has just approved.
    invalidate: [
      queryKeys.payments.all,
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.refunds.all,
      queryKeys.dashboard.all,
    ],
    onSuccess: () => onClose(),
  })

  // A rejection with no reason is not reviewable, so the server requires one and
  // so does this. Approving does not: "the money is there" needs no essay.
  const commentError =
    rejecting && comment.trim().length > 0 && comment.trim().length < 3
      ? 'Give at least a few words — this is the permanent record of the decision.'
      : undefined

  const canSubmit = (!rejecting || comment.trim().length >= 3) && !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={rejecting ? 'Reject this payment?' : 'Verify this payment?'}
      size="md"
      busy={mutation.isPending}
      description={
        rejecting
          ? 'Record that the money did not arrive. The booking stays unpaid and any points it used are returned to the customer.'
          : 'Confirm the money arrived. The booking is marked as paid and the amount becomes refundable.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant={rejecting ? 'danger' : 'primary'}
            iconLeft={rejecting ? <ShieldX aria-hidden="true" /> : <BadgeCheck aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate(undefined)}
          >
            {mutation.isPending
              ? rejecting
                ? 'Rejecting…'
                : 'Verifying…'
              : rejecting
                ? 'Reject payment'
                : 'Verify payment'}
          </Button>
        </>
      }
    >
      {payment && tripRequestId ? (
        <div className={styles.body}>
          <KeyValueGrid>
            <KeyValue label="Booking">{bookingReference ?? `#${tripRequestId}`}</KeyValue>
            <KeyValue label="Method">{describePaymentMethod(payment)}</KeyValue>
            <KeyValue label="Amount to confirm">
              <strong className={styles.amount}>
                {formatMoney(payment.externalAmount, payment.currency)}
              </strong>
            </KeyValue>
            {payment.pointsUsedMinor > 0 ? (
              <KeyValue label="Paid with points">
                {formatMoney(payment.pointsUsed, payment.currency)}
              </KeyValue>
            ) : null}
          </KeyValueGrid>

          <section className={styles.proof}>
            <h3 className={styles.sectionTitle}>Proof of payment</h3>
            <PaymentProofViewer
              tripRequestId={tripRequestId}
              hasProof={payment.hasProof}
              uploadedAt={payment.proofUploadedAt}
              sizeBytes={payment.proofSizeBytes}
            />
            {!payment.hasProof && !rejecting ? (
              <p className={styles.warning}>
                The customer has not uploaded anything yet. Only verify this if you have confirmed the
                transfer another way.
              </p>
            ) : null}
          </section>

          <Field
            label={rejecting ? 'Why is this being rejected?' : 'Comment (optional)'}
            hint={
              rejecting
                ? 'This is the permanent record of the decision.'
                : 'Recorded against the booking with your name and the time.'
            }
            required={rejecting}
            error={commentError}
          >
            {({ id, describedBy, invalid }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                rows={3}
                maxLength={1000}
                placeholder={
                  rejecting
                    ? 'No matching transfer on the account statement'
                    : 'Matched against the 14:02 transfer'
                }
                disabled={mutation.isPending}
              />
            )}
          </Field>
        </div>
      ) : null}
    </Dialog>
  )
}
